import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import Modal from "@/components/Modal";
import {
  EmptyState, ErrorMessage, PageHeader, Pagination, SearchInput, Toolbar,
} from "@/components/DataTableParts";
import {
  Phone, PlayCircle, Loader2, RefreshCw, IndianRupee, CheckCircle2,
  Copy, Download, FlaskConical,
} from "lucide-react";

const OUTCOMES = [
  { value: "INTERESTED", label: "Interested — proceed to payment" },
  { value: "CALLBACK", label: "Callback Requested" },
  { value: "NOT_INTERESTED", label: "Not Interested" },
  { value: "BUSY", label: "Busy" },
  { value: "NO_ANSWER", label: "No Answer" },
  { value: "WRONG_NUMBER", label: "Wrong Number" },
  { value: "ALREADY_SOLD_LAPTOP", label: "Already Sold Laptop" },
  { value: "PURCHASED_NEW_LAPTOP", label: "Purchased New Laptop" },
  { value: "HUMAN_REQUIRED", label: "Human Assistance Required" },
  { value: "OTHER", label: "Other" },
];

const STATUS_COLORS = {
  QUEUED: "bg-slate-100 text-slate-700",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
  CALLBACK: "bg-blue-100 text-blue-800",
  HUMAN_ASSIGNED: "bg-emerald-100 text-emerald-800",
  PAYMENT_PENDING: "bg-purple-100 text-purple-800",
  NOT_INTERESTED: "bg-rose-100 text-rose-700",
  WRONG_NUMBER: "bg-rose-100 text-rose-700",
  EXPIRED_UNCONVERTED: "bg-slate-200 text-slate-500",
  PAID: "bg-emerald-500 text-white",
};

const fmt = (v) => (v == null ? "—" : v);
const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—");
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—");

/** Absolute URL for certificate download (prepends REACT_APP_BACKEND_URL). */
const certUrl = (rel) => (rel ? `${API_BASE.replace(/\/api$/, "")}${rel}` : null);

export default function CallQueuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [assignedMine, setAssignedMine] = useState(false);
  const [logging, setLogging] = useState(null);
  const [form, setForm] = useState({ outcome: "INTERESTED", notes: "", callback_at: "", duration_seconds: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [scanMsg, setScanMsg] = useState("");
  const [payLead, setPayLead] = useState(null); // lead in "payment panel" modal
  const [payState, setPayState] = useState(null); // { loading, data, error }

  const [qDeb, setQDeb] = useState(q);
  useEffect(() => { const t = setTimeout(() => setQDeb(q), 300); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: 15 };
    if (qDeb) p.q = qDeb;
    if (status) p.status = status;
    if (assignedMine) p.assigned_to_me = true;
    return p;
  }, [page, qDeb, status, assignedMine]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/warranty-leads", { params })).data); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("CallQueuePage.load failed", err?.message || err);
      setData({ items: [], meta: null });
    }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true); setScanMsg("");
    try {
      const r = await api.post("/warranty-scan/run");
      setScanMsg(`Scan complete: ${r.data.leads_created} new · ${r.data.leads_skipped} existing across ${r.data.scanned_rules} rules.`);
      await load();
    } catch (e) { setScanMsg(errorToMessage(e)); }
    finally { setScanning(false); }
  };

  const openCall = (lead) => {
    setLogging(lead);
    setForm({ outcome: "INTERESTED", notes: "", callback_at: "", duration_seconds: "" });
    setFormError("");
  };

  const submitCall = async (e) => {
    e.preventDefault();
    setSubmitting(true); setFormError("");
    try {
      const payload = { outcome: form.outcome, notes: form.notes || null };
      if (form.duration_seconds) payload.duration_seconds = Number(form.duration_seconds);
      if (form.outcome === "CALLBACK" && form.callback_at) payload.callback_at = new Date(form.callback_at).toISOString();
      await api.post(`/warranty-leads/${logging.id}/call`, payload);
      setLogging(null);
      await load();
    } catch (err) { setFormError(errorToMessage(err)); }
    finally { setSubmitting(false); }
  };

  // ---------- Payment panel ----------
  const openPay = async (lead) => {
    setPayLead(lead);
    await fetchPayStatus(lead.id);
  };

  const fetchPayStatus = async (leadId) => {
    setPayState({ loading: true });
    try {
      const r = await api.get(`/warranty-leads/${leadId}/payment`);
      setPayState({ loading: false, data: r.data });
    } catch (e) { setPayState({ loading: false, error: errorToMessage(e) }); }
  };

  const sendPaymentLink = async () => {
    setPayState({ loading: true, data: payState?.data });
    try {
      await api.post(`/warranty-leads/${payLead.id}/payment-link`);
      await fetchPayStatus(payLead.id);
      await load();
    } catch (e) { setPayState({ loading: false, error: errorToMessage(e), data: payState?.data }); }
  };

  const simulatePayment = async () => {
    setPayState({ loading: true, data: payState?.data });
    try {
      await api.post(`/warranty-leads/${payLead.id}/simulate-payment`);
      await fetchPayStatus(payLead.id);
      await load();
    } catch (e) { setPayState({ loading: false, error: errorToMessage(e), data: payState?.data }); }
  };

  const verifyPayment = async () => {
    setPayState({ loading: true, data: payState?.data });
    try {
      await api.post(`/warranty-leads/${payLead.id}/verify-payment`);
      await fetchPayStatus(payLead.id);
      await load();
    } catch (e) { setPayState({ loading: false, error: errorToMessage(e), data: payState?.data }); }
  };

  const downloadCertificate = async (certRelUrl, certNo) => {
    // Cert routes are auth-guarded; do an authenticated blob fetch and force download.
    const auth = getStoredAuth();
    const url = `${API_BASE.replace(/\/api$/, "")}${certRelUrl}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${auth?.access_token}` } });
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `${certNo}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch (err) { if (process.env.NODE_ENV === "development") console.warn("clipboard copy failed", err?.message || err); }
  };

  return (
    <div>
      <PageHeader
        title="AI Call Queue"
        subtitle="Prioritised warranty renewal leads · click Log Call to record an outcome, or Payment to send a link."
        actions={
          <button onClick={runScan} disabled={scanning} data-testid="btn-run-scan"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm disabled:opacity-60">
            {scanning ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Run Eligibility Scan
          </button>
        }
      />
      {scanMsg && <div data-testid="scan-msg" className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{scanMsg}</div>}

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search by customer, mobile or serial…" />
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
          <option value="">All active</option>
          <option value="QUEUED">Queued</option>
          <option value="CALLBACK">Callback</option>
          <option value="HUMAN_ASSIGNED">Human Assigned</option>
          <option value="PAYMENT_PENDING">Payment Pending</option>
          <option value="PAID">Paid</option>
          <option value="EXPIRED_UNCONVERTED">Expired</option>
        </select>
        <label className="text-sm text-slate-700 flex items-center gap-2">
          <input type="checkbox" checked={assignedMine} onChange={(e) => setAssignedMine(e.target.checked)} data-testid="filter-mine" />
          Assigned to me
        </label>
      </Toolbar>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="queue-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              <th className="text-right px-3 py-2 font-medium">Prio</th>
              <th className="text-left px-3 py-2 font-medium">Customer</th>
              <th className="text-left px-3 py-2 font-medium">Laptop</th>
              <th className="text-right px-3 py-2 font-medium">Expiry</th>
              <th className="text-right px-3 py-2 font-medium">₹</th>
              <th className="text-left px-3 py-2 font-medium">Last Call</th>
              <th className="text-left px-3 py-2 font-medium">Assigned</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? (
              data.items.map((r) => (
                <tr key={r.id} data-testid={`row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{r.priority_score}</td>
                  <td className="px-3 py-2">{r.customer.full_name}<div className="text-xs text-slate-400 font-mono">{r.customer.mobile}</div></td>
                  <td className="px-3 py-2 text-slate-700">{fmt(r.passport.brand)} {fmt(r.passport.model)}<div className="text-xs text-slate-400">{r.passport.manufacturer_serial}</div></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtDate(r.expiry_date)}<div className="text-xs text-slate-400">{r.bucket}</div></td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{fmtMoney(r.computed_price)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.last_call ? (<><div>{fmtDateTime(r.last_call.dialed_at)}</div><div className="text-slate-500">#{r.last_call.attempt_no} · {r.last_call.outcome}</div></>) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.assigned_to_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>{r.status}</span>
                    <div className="text-xs text-slate-400 mt-0.5">Attempts: {r.attempts_count}</div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openCall(r)} data-testid={`log-call-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-2 py-1 text-xs">
                        <Phone size={12} /> Call
                      </button>
                      <button onClick={() => openPay(r)} data-testid={`payment-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-xs">
                        <IndianRupee size={12} /> Payment
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={9}><EmptyState label="No leads. Click Run Eligibility Scan to generate." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />

      {/* --- Log Call modal --- */}
      <Modal open={!!logging} onClose={() => setLogging(null)} title={logging ? `Log Call · ${logging.customer.full_name}` : ""} size="lg">
        {logging && (
          <form onSubmit={submitCall} className="space-y-3" data-testid="call-form">
            <ErrorMessage message={formError} />
            <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
              <div><span className="text-slate-500">Mobile:</span> <span className="font-mono">{logging.customer.mobile}</span></div>
              <div><span className="text-slate-500">Laptop:</span> {logging.passport.brand} {logging.passport.model}</div>
              <div><span className="text-slate-500">Warranty Expiry:</span> {fmtDate(logging.expiry_date)} ({logging.bucket})</div>
              <div><span className="text-slate-500">Renewal Amount:</span> <span className="text-emerald-700 font-medium">{fmtMoney(logging.computed_price)}</span></div>
              <div><span className="text-slate-500">Order Value:</span> {fmtMoney(logging.order_value_snapshot)}</div>
              <div><span className="text-slate-500">Attempts so far:</span> {logging.attempts_count}</div>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Outcome</label>
              <select required className="input" value={form.outcome}
                onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="field-outcome">
                {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.outcome === "CALLBACK" && (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Callback At (leave empty for default +24h)</label>
                <input type="datetime-local" className="input" value={form.callback_at}
                  onChange={(e) => setForm({ ...form, callback_at: e.target.value })} data-testid="field-callback" />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-600 mb-1">Duration (seconds)</label>
              <input type="number" min={0} max={3600} className="input" value={form.duration_seconds}
                onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })} data-testid="field-duration" />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Notes</label>
              <textarea rows={3} className="input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="field-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setLogging(null)} className="px-3 py-1.5 text-sm rounded-md border border-slate-300">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="call-submit"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={14} /> : <PlayCircle size={14} />} Submit
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* --- Payment panel modal --- */}
      <Modal open={!!payLead} onClose={() => { setPayLead(null); setPayState(null); }}
             title={payLead ? `Payment · ${payLead.customer.full_name}` : ""} size="lg">
        {payLead && (
          <div className="space-y-4" data-testid="payment-panel">
            {payState?.error && <ErrorMessage message={payState.error} />}
            <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
              <div><span className="text-slate-500">Mobile:</span> <span className="font-mono">{payLead.customer.mobile}</span></div>
              <div><span className="text-slate-500">Laptop:</span> {payLead.passport.brand} {payLead.passport.model}</div>
              <div><span className="text-slate-500">Warranty Expiry:</span> {fmtDate(payLead.expiry_date)}</div>
              <div><span className="text-slate-500">Amount:</span> <span className="text-emerald-700 font-medium">{fmtMoney(payLead.computed_price)}</span></div>
            </div>

            {payState?.loading && !payState?.data && (
              <div className="text-center py-6 text-slate-500"><Loader2 className="inline animate-spin" size={16} /> Loading…</div>
            )}

            {payState?.data && (() => {
              const s = payState.data;
              const st = s.lead_status;
              return (
                <div className="space-y-4">
                  {/* Step indicator */}
                  <div className="flex items-center gap-2 text-xs" data-testid="payment-steps">
                    <Step done={st !== "HUMAN_ASSIGNED"} label="1. Link Sent" />
                    <ArrowSep />
                    <Step done={st === "PAID"} label="2. Payment Received" />
                    <ArrowSep />
                    <Step done={st === "PAID"} label="3. Warranty Extended" />
                    <ArrowSep />
                    <Step done={!!s.certificate_no} label="4. Certificate Issued" />
                  </div>

                  {/* HUMAN_ASSIGNED: send link */}
                  {st === "HUMAN_ASSIGNED" && (
                    <div className="border border-slate-200 rounded-md p-4">
                      <div className="text-sm text-slate-700 mb-3">
                        No payment link yet. Generate a Razorpay payment link for <strong>{fmtMoney(payLead.computed_price)}</strong>.
                      </div>
                      <button onClick={sendPaymentLink} disabled={payState.loading} data-testid="btn-send-link"
                        className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-sm disabled:opacity-60">
                        {payState.loading ? <Loader2 className="animate-spin" size={14}/> : <IndianRupee size={14}/>} Send Payment Link
                      </button>
                    </div>
                  )}

                  {/* PAYMENT_PENDING: show link, allow verify + sandbox simulate */}
                  {st === "PAYMENT_PENDING" && (
                    <div className="border border-slate-200 rounded-md p-4 space-y-3">
                      <div>
                        <div className="text-xs text-slate-500 uppercase mb-1">Payment Link</div>
                        <div className="flex items-center gap-2">
                          <input readOnly value={s.link_url || ""} data-testid="link-url"
                            className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-mono" />
                          <button onClick={() => copy(s.link_url)} data-testid="btn-copy-link"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 hover:bg-slate-100 px-2 py-1.5 text-xs">
                            <Copy size={12}/> Copy
                          </button>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Amount: {fmtMoney(s.amount)} · Provider ref: {s.provider_order_id}</div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button onClick={verifyPayment} disabled={payState.loading} data-testid="btn-verify"
                          className="inline-flex items-center gap-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm disabled:opacity-60">
                          {payState.loading ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>} Recheck Payment Status
                        </button>
                        <button onClick={simulatePayment} disabled={payState.loading} data-testid="btn-simulate"
                          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 px-3 py-1.5 text-sm disabled:opacity-60">
                          {payState.loading ? <Loader2 className="animate-spin" size={14}/> : <FlaskConical size={14}/>} Simulate Payment (Sandbox)
                        </button>
                      </div>
                      <div className="text-xs text-slate-500">
                        Sandbox mode is active. Real Razorpay checkout will replace this once live keys are configured — no code change required.
                      </div>
                    </div>
                  )}

                  {/* PAID: certificate + coverage */}
                  {st === "PAID" && (
                    <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 space-y-3" data-testid="paid-panel">
                      <div className="flex items-center gap-2 text-emerald-800 font-medium">
                        <CheckCircle2 size={18}/> Payment received — warranty extended
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Certificate No:</span> <span className="font-mono">{s.certificate_no}</span></div>
                        <div><span className="text-slate-500">Payment Ref:</span> <span className="font-mono text-xs">{s.provider_payment_id || s.provider_order_id}</span></div>
                        <div><span className="text-slate-500">Coverage Starts:</span> {fmtDate(s.coverage_starts_on)}</div>
                        <div><span className="text-slate-500">Coverage Ends:</span> {fmtDate(s.coverage_ends_on)}</div>
                      </div>
                      <button onClick={() => downloadCertificate(s.certificate_url, s.certificate_no)}
                        data-testid="btn-download-cert"
                        className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm">
                        <Download size={14}/> Download Warranty Certificate
                      </button>
                    </div>
                  )}

                  {/* Terminal / not-eligible states */}
                  {["QUEUED", "IN_PROGRESS", "CALLBACK", "NOT_INTERESTED", "WRONG_NUMBER",
                    "EXPIRED_UNCONVERTED"].includes(st) && (
                    <div className="text-sm text-slate-600 border border-slate-200 rounded-md p-4">
                      Lead is in status <strong>{st}</strong>. Log a call with outcome <em>Interested</em> first before sending a payment link.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Step({ done, label }) {
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded ${done ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
      {done ? <CheckCircle2 size={12}/> : <span className="w-2 h-2 rounded-full bg-slate-400 inline-block"/>}
      {label}
    </div>
  );
}
function ArrowSep() { return <span className="text-slate-300">›</span>; }
